import * as React from 'react';
import styled from 'styled-components';
import { LoadingIndicator } from '../LoadingIndicator';
import { COLORS, TYPOGRAPHY } from '../styles';

const Primary = styled.button<ButtonProps>`
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  min-height: ${props => (props.size === 'big' ? '56px' : props.size === 'small' ? '40px' : '44px')};

  &.loading-state {
    cursor: wait;
  }

  &:focus:not(:focus-visible),
  &:focus:not(:focus-visible) > div.button-content {
    outline: 0 !important;
  }

  div.button-content {
    padding: ${props => (props.size === 'big' ? '13px 18px' : props.size === 'small' ? '4.5px 10px' : '7px 14px')};
    margin: ${props => (props.size === 'big' ? '0' : props.size === 'small' ? '8px 0' : '4px 0')};
    color: white;
    background-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    border-radius: 8px;
    border-width: 2px;
    border-style: solid;
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    font-family: ${TYPOGRAPHY.fontFamily};
    font-weight: bold;
    font-size: ${props => (props.size === 'big' ? '22px' : props.size === 'small' ? '16px' : '19px')};
    line-height: ${props => (props.size === 'big' ? '26.4px' : props.size === 'small' ? '19.2px' : '22.8px')};
    width: ${props => props.width};

    position: relative;
    span.loading {
      opacity: 0;
    }
    div {
      position: absolute;
      top: 0;
      left: calc(50% - ${props => (props.size === 'big' ? '13.2px' : props.size === 'small' ? '9.6px' : '11.4px')});
      svg {
        height: ${props => (props.size === 'big' ? '26.4px' : props.size === 'small' ? '19.2px' : '22.8px')};
        width: ${props => (props.size === 'big' ? '26.4px' : props.size === 'small' ? '19.2px' : '22.8px')};
        path {
          fill: white;
        }
      }
    }
  }

  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
  }
  &:focus > div.button-content,
  &.focus-state > div.button-content {
    box-shadow: 0px 4px 12px ${COLORS.focus_25}, 0px 0px 8px ${COLORS.focus} !important;
  }

  &:disabled, 
  &.disabled-state{
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: ${COLORS.neutral_100};
    color: ${COLORS.neutral_300};
    border-color: ${COLORS.neutral_100};
    cursor: not-allowed;
    &:hover,
    &:active {
      background-color: ${COLORS.neutral_100};
      color: ${COLORS.neutral_300};
      border-color: ${COLORS.neutral_100};
    }
  }
`;

const Secondary = styled(Primary)<ButtonProps>`
  div.button-content {
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    background-color: white;

    div svg path {
      fill: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    }
  }

  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: white;
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
    div {
      svg {
        path {
          fill: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
        }
      }
    }
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: white;
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.primary_800 : COLORS.primary_800)};
    div {
      svg {
        path {
          fill: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
        }
      }
    }
  }
  &:disabled, 
  &.disabled-state{
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: white !important;
    color: ${COLORS.neutral_300};
    border-color: ${COLORS.neutral_100};
    cursor: not-allowed;
  }
`;

const Tertiary = styled(Primary)<ButtonProps>`
  div.button-content {
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    border-color: transparent;
    background-color: white;
    border-width: 1px;
    div svg path {
      fill: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
    }
  }

  &:hover > div.button-content,
  &.hover-state > div.button-content {
    background-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_20 : COLORS.primary_20)};
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_20 : COLORS.primary_20)};
    div {
      svg {
        path {
          fill: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
        }
      }
    }
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    background-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_100 : COLORS.primary_100)};
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
    border-color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_100 : COLORS.primary_100)};
    div {
      svg {
        path {
          fill: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
        }
      }
    }
  }
  &:disabled, 
  &.disabled-state{
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: white !important;
    color: ${COLORS.neutral_300};
    border-color: transparent;
    cursor: not-allowed;
  }
  &:disabled, 
  &.disabled-state{
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: white !important;
    color: ${COLORS.neutral_300};
    border-color: transparent !important;
    cursor: not-allowed;
  }
`;

const Correct = styled(Primary)<ButtonProps>`
  div.button-content {
    color: white;
    border-color: ${COLORS.correct_500};
    background-color: ${COLORS.correct_500};
  }
  &:hover > div.button-content,
  &.hover-state > div.button-content {
    border-color: ${COLORS.correct_700};
    background-color: ${COLORS.correct_700};
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    border-color: ${COLORS.correct_800};
    background-color: ${COLORS.correct_800};
  }

  &:disabled, 
  &.disabled-state{
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: ${COLORS.neutral_100};
    color: ${COLORS.neutral_300};
    border-color: ${COLORS.neutral_100};
    cursor: not-allowed;
    &:hover,
    &:active {
      background-color: ${COLORS.neutral_100};
      color: ${COLORS.neutral_300};
      border-color: ${COLORS.neutral_100};
    }
  }
`;

const Critical = styled(Primary)<ButtonProps>`
  div.button-content {
    color: white;
    border-color: ${COLORS.critical_500};
    background-color: ${COLORS.critical_500};
  }
  &:hover > div.button-content,
  &.hover-state > div.button-content {
    border-color: ${COLORS.critical_700};
    background-color: ${COLORS.critical_700};
  }
  &:active > div.button-content,
  &.active-state > div.button-content {
    border-color: ${COLORS.critical_800};
    background-color: ${COLORS.critical_800};
  }

  &:disabled, 
  &.disabled-state{
    cursor: not-allowed;
  }
  &:disabled > div.button-content,
  &.disabled-state > div.button-content {
    background-color: ${COLORS.neutral_100};
    color: ${COLORS.neutral_300};
    border-color: ${COLORS.neutral_100};
    cursor: not-allowed;
    &:hover,
    &:active {
      background-color: ${COLORS.neutral_100};
      color: ${COLORS.neutral_300};
      border-color: ${COLORS.neutral_100};
    }
  }
`;

const Text = styled.button<ButtonProps>`
  color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1 : COLORS.primary)};
  background-color: transparent;
  padding: 0;
  border: none;
  text-decoration: underline;
  font-size: ${props => (props.size === 'big' ? '22px' : props.size === 'small' ? '16px' : '19px')};
  line-height: ${props => (props.size === 'big' ? '26.4px' : props.size === 'small' ? '19.2px' : '22.8px')};
  font-weight: bold;
  font-family: inherit;
  cursor: pointer;

  &:hover,
  &:focus,
  &.hover-state,
  &.focus-state {
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_700 : COLORS.primary_700)};
    background: transparent;
  }
  &:active,
  &.active-state {
    color: ${props => (props.colorTheme === 'teal' ? COLORS.accent1_800 : COLORS.primary_800)};
    background: transparent;
  }
  &:disabled,
  &.disabled-state {
    color: ${COLORS.neutral_500};
    background: transparent;
    cursor: not-allowed;
  }
`;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'text' | 'correct' | 'critical';
  role?: 'button' | string;
  title?: string;
  size?: 'small' | 'normal' | 'big';
  colorTheme?: 'teal';
  width?: 'auto' | string;
  loading?: boolean;
  testId?: string;
}

const Button: React.FunctionComponent<ButtonProps> = ({ children = 'Primary Button', variant = 'primary', type = 'button', size = 'normal', width = 'auto', testId, ...props }) => {
  // Let's filter out properties that we don't need to render.
  const { loading, ...renderProps } = props;

  // Let's render button
  switch (variant) {
    case 'primary':
      return (
        <Primary {...renderProps} type={type} size={size} width={width} data-testid={testId} className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}>
          <div className="button-content">
            {!props.loading && children}
            {props.loading && (
              <>
                <span className="loading">{children}</span>
                <LoadingIndicator></LoadingIndicator>
              </>
            )}
          </div>
        </Primary>
      );
    case 'secondary':
      return (
        <Secondary
          {...renderProps}
          type={type}
          size={size}
          width={width}
          data-testid={testId}
          className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}>
          <div className="button-content">
            {!props.loading && children}
            {props.loading && (
              <>
                <span className="loading">{children}</span>
                <LoadingIndicator></LoadingIndicator>
              </>
            )}
          </div>
        </Secondary>
      );
    case 'tertiary':
      return (
        <Tertiary
          {...renderProps}
          type={type}
          size={size}
          width={width}
          data-testid={testId}
          className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}>
          <div className="button-content">
            {!props.loading && children}
            {props.loading && (
              <>
                <span className="loading">{children}</span>
                <LoadingIndicator></LoadingIndicator>
              </>
            )}
          </div>
        </Tertiary>
      );
    case 'text':
      return (
        <Text {...renderProps} type={type} size={size} width={width} data-testid={testId}>
          {children}
        </Text>
      );
    case 'correct':
      return (
        <Correct {...renderProps} type={type} size={size} width={width} data-testid={testId} className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}>
          <div className="button-content">
            {!props.loading && children}
            {props.loading && (
              <>
                <span className="loading">{children}</span>
                <LoadingIndicator></LoadingIndicator>
              </>
            )}
          </div>
        </Correct>
      );
    case 'critical':
      return (
        <Critical
          {...renderProps}
          type={type}
          size={size}
          width={width}
          data-testid={testId}
          className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}>
          <div className="button-content">
            {!props.loading && children}
            {props.loading && (
              <>
                <span className="loading">{children}</span>
                <LoadingIndicator></LoadingIndicator>
              </>
            )}
          </div>
        </Critical>
      );
    default:
      return (
        <Primary {...renderProps} type={type} size={size} width={width} data-testid={testId} className={props.loading ? 'loading-state ' + props.className : ' ' + props.className}>
          <div className="button-content">
            {!props.loading && children}
            {props.loading && (
              <>
                <span className="loading">{children}</span>
                <LoadingIndicator></LoadingIndicator>
              </>
            )}
          </div>
        </Primary>
      );
  }
};

export default Button;
